/*
 * 富消息渲染。
 *
 * 严格按 @dreamverse/contracts 的 8 种 MessagePart 分支，不多认也不少认。
 * 遇到未来新增的 part 类型时降级为一行「暂不支持的内容」而不是白屏或崩溃——
 * 契约承诺「新增字段一律可选，已有字段永不删除或改变语义」，但没承诺
 * 不会新增 part 类型。
 *
 * 呈现分级（features/skills/README.md）：
 *   ambient / metered  —— 不渲染任何 Skill UI，结果已融入回复文本；
 *   guarded            —— 才会收到 skill_progress / skill_result。
 * 前端不得自作主张为 ambient / metered 补「正在搜索…」这类过程提示。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { MessagePart } from "@dreamverse/contracts";
import {
  IconAlert,
  IconImage,
  IconMic,
  IconMusic,
  IconPlay,
  IconRetry,
  IconSpark,
  IconStop,
} from "./Icons";
import { Butterfly } from "./AsyncState";
import { ImageViewer } from "./ImageViewer";
import { musicTrackOf, useMusicPlayer } from "./MusicPlayer";
import { apiRequest } from "../services/api-client";

/* -------------------------------------------------------------------------- */
/* 资产                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * 受控资产图片。
 *
 * 资产走后端签发的访问 URL，不是公开直链。后端不可达时连 URL 都拿不到，
 * 这里显示占位而不是碎图标——碎图标看起来像「这张图坏了」，
 * 实际情况是「还没能问到这张图在哪」。
 */
const IMAGE_LOAD_TIMEOUT_MS = 24_000;
const IMAGE_LOADING_LABEL_DELAY_MS = 240;
const IMAGE_AUTO_RETRY_DELAYS_MS = [600, 1_400, 3_000] as const;
const IMAGE_MAX_AUTOMATIC_ATTEMPTS = IMAGE_AUTO_RETRY_DELAYS_MS.length + 1;
const IMAGE_PERSISTENCE_POLL_MS = 1_200;

interface AssetDisplayState {
  persistencePending: boolean;
}

export function AssetImage({
  assetId,
  alt,
  initialSrc,
  onInitialSrcConsumed,
}: {
  assetId: string;
  alt: string;
  /** 刚上传时已经显示过的本地 Object URL；服务器预览在后台预载，不遮掉它。 */
  initialSrc?: string;
  onInitialSrcConsumed?: (src: string) => void;
}) {
  const [failed, setFailed] = useState(false);
  const [usingInitialSrc, setUsingInitialSrc] = useState(Boolean(initialSrc));
  const [loaded, setLoaded] = useState(Boolean(initialSrc));
  const [showLoadingLabel, setShowLoadingLabel] = useState(false);
  const [attempt, setAttempt] = useState(1);
  const [sourceRevision, setSourceRevision] = useState(0);
  const [nearViewport, setNearViewport] = useState(Boolean(initialSrc));
  const [viewerOpen, setViewerOpen] = useState(false);
  const figureRef = useRef<HTMLElement>(null);
  const retryTimerRef = useRef<number | null>(null);
  const sawPersistencePendingRef = useRef(false);
  const baseUrl = `/api/v1/app/assets/${encodeURIComponent(assetId)}`;
  const previewUrl = `${baseUrl}/preview`;
  const previewContentUrl = `${baseUrl}/preview-content`;
  // 先让浏览器直接读 OSS；第二次仍走直链以吸收短暂网络抖动，之后改由 API
  // 鉴权回源缩略图。此前所有“重试”都只会命中同一条 OSS 线路。
  const candidateSrc =
    attempt === 1
      ? previewUrl
      : attempt === 2
        ? `${previewUrl}?retry=${attempt}`
        : `${previewContentUrl}?retry=${attempt}`;
  // 临时地址与 OSS 地址共用同一个 assetId。后台切换完成后改变 img.src，才能中止
  // 仍在等待的供应商请求并重新经过 `/preview` 命中 OSS；正常稳定图片不增加 query，
  // 继续复用图库和聊天之间的浏览器缓存。
  const serverSrc =
    sourceRevision === 0
      ? candidateSrc
      : `${candidateSrc}${candidateSrc.includes("?") ? "&" : "?"}storage=${sourceRevision}`;
  const src = usingInitialSrc && initialSrc ? initialSrc : serverSrc;

  useEffect(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setFailed(false);
    setUsingInitialSrc(Boolean(initialSrc));
    setLoaded(Boolean(initialSrc));
    setShowLoadingLabel(false);
    setAttempt(1);
    setSourceRevision(0);
    sawPersistencePendingRef.current = false;
    setNearViewport(Boolean(initialSrc));
    setViewerOpen(false);
  }, [baseUrl]);

  /*
   * 用户刚选图时浏览器已经有完整本地像素，不能为了换成服务器 URL 把它盖回
   * “图片加载中”。本地图保持可见，同时预载鉴权后的 OSS 缩略图；预载完成后
   * 再无缝切换。若服务器暂时失败，本地图继续留着，不影响本轮阅读。
   */
  useEffect(() => {
    if (!initialSrc || !usingInitialSrc) return;
    let disposed = false;
    const preload = new Image();
    preload.onload = () => {
      if (disposed) return;
      setAttempt(1);
      setFailed(false);
      setLoaded(true);
      setUsingInitialSrc(false);
    };
    preload.src = previewUrl;
    return () => {
      disposed = true;
      preload.onload = null;
      preload.onerror = null;
    };
  }, [initialSrc, previewUrl, usingInitialSrc]);

  useEffect(
    () => () => {
      if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    },
    [],
  );

  // 缓存命中的图片通常会在一帧内完成，不为这类刷新闪一次“加载中”。
  useEffect(() => {
    if (loaded || failed || !nearViewport) {
      setShowLoadingLabel(false);
      return;
    }
    const timer = window.setTimeout(
      () => setShowLoadingLabel(true),
      IMAGE_LOADING_LABEL_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [failed, loaded, nearViewport, src]);

  /*
   * `loading=lazy` 的请求只有靠近视口才真正开始。计时也必须从那时起：
   * 历史里还没滚到的图片不应在后台被误判成超时。
   */
  useEffect(() => {
    const node = figureRef.current;
    if (!node || nearViewport) return;
    if (typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: "240px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [nearViewport]);

  /*
   * 供应商临时地址可以让图片在 OSS 保存前就出现，但某些网络下它本身下载很慢。
   * 后台保存可能已经完成，原来的 `<img>` 却不知道 URL 的数据源变了，仍会一直等到
   * 24 秒超时。只在“可见且未加载”阶段读取不含敏感 URL 的状态；一旦 OSS 就绪，
   * 立刻换一个 src 重新请求，获得与右上角图库相同的 1280px WebP。
   */
  useEffect(() => {
    if (!nearViewport || loaded || failed) return;

    const controller = new AbortController();
    let pollTimer: number | null = null;
    let disposed = false;

    const scheduleNext = (): void => {
      if (disposed) return;
      pollTimer = window.setTimeout(() => void checkState(), IMAGE_PERSISTENCE_POLL_MS);
    };

    const checkState = async (): Promise<void> => {
      try {
        const state = await apiRequest<AssetDisplayState>(`${baseUrl}/display-state`, {
          signal: controller.signal,
          timeoutMs: 5_000,
        });
        if (disposed) return;
        if (state.persistencePending) {
          sawPersistencePendingRef.current = true;
          scheduleNext();
          return;
        }
        if (sawPersistencePendingRef.current) {
          sawPersistencePendingRef.current = false;
          if (retryTimerRef.current !== null) {
            window.clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
          }
          setFailed(false);
          setShowLoadingLabel(false);
          setAttempt(1);
          setSourceRevision((value) => value + 1);
        }
      } catch {
        // 状态查询只是加速通道；失败时保留原图片请求，并稍后再试，不把一张本来能
        // 加载的图误判成失败。
        if (!disposed && !controller.signal.aborted) scheduleNext();
      }
    };

    void checkState();
    return () => {
      disposed = true;
      controller.abort();
      if (pollTimer !== null) window.clearTimeout(pollTimer);
    };
  }, [baseUrl, failed, loaded, nearViewport]);

  /*
   * `<img>` 没有原生超时。OSS 请求若一直 pending，onError 永远不会来；
   * 超时与 onError 共用自动降级；所有线路都失败后才把重试交给用户，不能永久
   * 停在 opacity:0 + disabled。
   */
  const handleLoadFailure = useCallback((): void => {
    setLoaded(false);
    if (retryTimerRef.current !== null) return;
    if (attempt >= IMAGE_MAX_AUTOMATIC_ATTEMPTS) {
      setFailed(true);
      return;
    }
    const delayMs = IMAGE_AUTO_RETRY_DELAYS_MS[attempt - 1] ?? 3_000;
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      setShowLoadingLabel(false);
      setAttempt((value) => value + 1);
    }, delayMs);
  }, [attempt]);

  useEffect(() => {
    if (!nearViewport || loaded || failed) return;
    const timer = window.setTimeout(handleLoadFailure, IMAGE_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [failed, handleLoadFailure, loaded, nearViewport, src]);

  const retry = (): void => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setFailed(false);
    setLoaded(false);
    setShowLoadingLabel(false);
    // 这是一次用户明确发起的请求；不再交给 lazy-load 猜它是否靠近视口。
    setNearViewport(true);
    setAttempt((value) => value + 1);
  };

  if (failed) {
    return (
      <figure className="asset asset--failed">
        <button type="button" className="asset__retry" onClick={retry}>
          <span className="asset__glyph">
            <IconImage size={20} />
          </span>
          <span className="asset__failure-copy">
            <strong>图片没有加载出来</strong>
            <span>点一下重新加载</span>
          </span>
          <IconRetry size={17} />
        </button>
      </figure>
    );
  }

  return (
    <>
      <figure
        ref={figureRef}
        className={`asset${loaded ? " is-loaded" : " is-loading"}`}
        aria-busy={!loaded}
      >
        <button
          type="button"
          className="asset__open"
          onClick={() => (loaded ? setViewerOpen(true) : retry())}
          aria-label={
            loaded
              ? `查看大图：${alt}`
              : "图片正在加载，点一下重新加载"
          }
        >
          <img
            key={src}
            src={src}
            alt={alt}
            loading={usingInitialSrc || attempt > 1 ? "eager" : "lazy"}
            onLoad={() => {
              if (retryTimerRef.current !== null) {
                window.clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
              }
              setLoaded(true);
              setShowLoadingLabel(false);
              if (!usingInitialSrc && initialSrc) onInitialSrcConsumed?.(initialSrc);
            }}
            onError={handleLoadFailure}
          />
          {!loaded && showLoadingLabel ? (
            <span className="asset__loading" aria-live="polite">
              <IconImage size={20} />
              <span>图片加载中……</span>
            </span>
          ) : null}
        </button>
      </figure>
      <ImageViewer
        open={viewerOpen}
        src={baseUrl}
        fallbackSrc={`${baseUrl}/content`}
        alt={alt}
        ariaLabel={alt || "图片查看器"}
        downloadUrl={`${baseUrl}/download`}
        downloadName={`dreamverse-${assetId.slice(0, 8)}`}
        onClose={() => setViewerOpen(false)}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 各类 part                                                                   */
/* -------------------------------------------------------------------------- */

function TextBlock({ text }: { text: string }) {
  // 换行保留，但不解析 Markdown：缈缈的表达约束里 noRubbish / noComments
  // 要求不出现列表和小标题，把回复当 Markdown 渲染会鼓励模型输出结构化文本。
  return <p className="part-text">{text}</p>;
}

function PaintFlightCard({ executionId }: { executionId: string }) {
  return (
    <Link
      className="paint-flight-card"
      to={`/skill/${executionId}`}
      aria-label="缈缈正在画画，查看任务"
    >
      <span className="paint-flight-card__sky" aria-hidden>
        <span className="paint-flight-card__butterfly paint-flight-card__butterfly--1">
          <Butterfly />
        </span>
        <span className="paint-flight-card__butterfly paint-flight-card__butterfly--2">
          <Butterfly />
        </span>
        <span className="paint-flight-card__butterfly paint-flight-card__butterfly--3">
          <Butterfly />
        </span>
      </span>
    </Link>
  );
}

function CitationBlock({
  url,
  title,
  summary,
  fetchedAt,
}: {
  url: string;
  title: string;
  summary?: string;
  fetchedAt: string;
}) {
  return (
    <a className="part-citation" href={url} target="_blank" rel="noreferrer">
      <span className="part-citation__title">{title}</span>
      {summary ? (
        <span className="part-citation__summary">{summary}</span>
      ) : null}
      {/* 抓取时间是判断时效的依据：外部内容随时可能已经变了。 */}
      <span className="part-citation__meta">
        抓取于 {new Date(fetchedAt).toLocaleString("zh-CN")}
      </span>
    </a>
  );
}

const SKILL_STATUS_LABEL: Record<string, string> = {
  awaiting_confirmation: "等待你确认",
  queued: "排队中",
  running: "进行中",
  succeeded: "已完成",
  failed: "失败",
  cancelled: "已取消",
};

const SKILL_NAME: Record<string, string> = {
  "dream.paint": "画画",
  "dream.paint.pro": "画画",
};

export function MessagePartRenderer({
  part,
  onRetrySkill,
  retryingSkillExecutionId,
  imagePreviewUrls,
  onImagePreviewConsumed,
}: {
  part: MessagePart;
  onRetrySkill?: (executionId: string) => void;
  retryingSkillExecutionId?: string | null;
  imagePreviewUrls?: ReadonlyMap<string, string>;
  onImagePreviewConsumed?: (assetId: string, src: string) => void;
}) {
  const musicPlayer = useMusicPlayer();

  switch (part.type) {
    case "text":
      return <TextBlock text={part.text} />;

    case "image":
      return (
        <AssetImage
          assetId={part.assetId}
          alt={part.alt}
          {...(imagePreviewUrls?.get(part.assetId)
            ? { initialSrc: imagePreviewUrls.get(part.assetId)! }
            : {})}
          onInitialSrcConsumed={(src) => onImagePreviewConsumed?.(part.assetId, src)}
        />
      );

    case "audio":
      return (
        <div className="part-audio">
          <span className="part-audio__glyph">
            <IconMic size={16} />
          </span>
          <div>
            <strong>{part.title ?? (part.audioKind === "singing_clip" ? "清唱片段" : "语音")}</strong>
            {part.artist || part.versionName ? (
              <span className="part-audio__meta">
                {[part.artist, part.versionName].filter(Boolean).join(" · ")}
              </span>
            ) : null}
            {part.transcript ? <p>{part.transcript}</p> : null}
          </div>
          {part.durationMs ? (
            <span className="part-audio__dur">
              {Math.round(part.durationMs / 1000)}″
            </span>
          ) : null}
          {part.playable !== false && musicPlayer.supported ? (
            <audio
              className="part-audio__player"
              controls
              preload="metadata"
              src={
                part.recordingId
                  ? `/api/v1/app/music/recordings/${encodeURIComponent(part.recordingId)}/audio`
                  : `/api/v1/app/assets/${encodeURIComponent(part.assetId)}/content`
              }
              onPlay={musicPlayer.pause}
            />
          ) : (
            <span className="chip chip--muted">当前环境无法播放</span>
          )}
        </div>
      );

    case "music": {
      const isThisTrackLoading =
        musicPlayer.track?.recordingId === part.trackId && musicPlayer.isLoading;
      const isThisTrackPlaying =
        musicPlayer.track?.recordingId === part.trackId && musicPlayer.isPlaying;
      return (
        <div className="part-music">
          <span className="part-music__glyph">
            <IconMusic size={18} />
          </span>
          <div className="part-music__meta">
            <strong>{part.title}</strong>
            {part.artist || part.versionName ? (
              <span>{[part.artist, part.versionName].filter(Boolean).join(" · ")}</span>
            ) : null}
          </div>
          {part.playable && musicPlayer.supported ? (
            <button
              type="button"
              className={`part-music__play${isThisTrackPlaying ? " is-playing" : ""}`}
              disabled={isThisTrackLoading}
              onClick={() => {
                if (isThisTrackPlaying) musicPlayer.stop();
                else musicPlayer.playTrack(musicTrackOf(part));
              }}
              aria-label={`${isThisTrackLoading ? "正在加载" : isThisTrackPlaying ? "停止" : "播放"}${part.title}`}
              aria-pressed={isThisTrackPlaying}
              aria-busy={isThisTrackLoading}
            >
              <span className="part-music__play-content">
                {isThisTrackLoading ? (
                  <i className="part-music__loading-spinner" aria-hidden />
                ) : isThisTrackPlaying ? (
                  <IconStop size={17} />
                ) : (
                  <IconPlay size={17} />
                )}
                {isThisTrackLoading ? "加载" : isThisTrackPlaying ? "停止" : "播放"}
              </span>
            </button>
          ) : (
            <span className="chip chip--muted">当前环境无法播放</span>
          )}
        </div>
      );
    }

    case "card":
      return (
        <div
          className={`part-card${
            part.cardType === "channel_quote" ? " part-card--quote" : ""
          }`}
        >
          <strong>{part.title}</strong>
          {part.description ? <p>{part.description}</p> : null}
        </div>
      );

    case "citation":
      return (
        <CitationBlock
          url={part.url}
          title={part.title}
          fetchedAt={part.fetchedAt}
          {...(part.summary === undefined ? {} : { summary: part.summary })}
        />
      );

    case "skill_progress":
      if (
        (part.skillKey === "dream.paint" || part.skillKey === "dream.paint.pro") &&
        (part.status === "queued" || part.status === "running")
      ) {
        return <PaintFlightCard executionId={part.executionId} />;
      }
      return (
        <div className="part-skill part-skill--running">
          <span className="part-skill__glyph">
            <IconSpark size={16} />
          </span>
          <div>
            <strong>{SKILL_NAME[part.skillKey] ?? part.skillKey}</strong>
            <span>{SKILL_STATUS_LABEL[part.status] ?? part.status}</span>
            {part.message ? <p>{part.message}</p> : null}
            <Link className="part-skill__detail" to={`/skill/${part.executionId}`}>
              查看任务
            </Link>
          </div>
        </div>
      );

    case "skill_result":
      // 画画成功消息已经同时带有 text + image；再画一张“已完成 / 查看任务”卡
      // 只会重复信息，并在 AVG 的定高正文里额外占掉几页。失败态仍保留详情入口，
      // 用户需要在那里查看错误与是否可重试。
      if (
        (part.skillKey === "dream.paint" || part.skillKey === "dream.paint.pro") &&
        part.status === "succeeded"
      ) {
        return null;
      }
      const retrying = retryingSkillExecutionId === part.executionId;
      const isPaint =
        part.skillKey === "dream.paint" || part.skillKey === "dream.paint.pro";
      return (
        <div
          className={`part-skill part-skill--${
            part.status === "succeeded" ? "ok" : "bad"
          }`}
        >
          <span className="part-skill__glyph">
            {part.status === "succeeded" ? (
              <IconSpark size={16} />
            ) : (
              <IconAlert size={16} />
            )}
          </span>
          <div>
            <strong>{SKILL_NAME[part.skillKey] ?? part.skillKey}</strong>
            <span>{SKILL_STATUS_LABEL[part.status] ?? part.status}</span>
            {part.summary ? <p>{part.summary}</p> : null}
            <Link className="part-skill__detail" to={`/skill/${part.executionId}`}>
              查看任务
            </Link>
          </div>
          {part.retryable && onRetrySkill ? (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={retrying}
              aria-busy={retrying}
              onClick={() => onRetrySkill(part.executionId)}
            >
              <IconRetry size={14} />
              {retrying ? "正在重新生成…" : isPaint ? "重新生成" : "重试"}
            </button>
          ) : null}
        </div>
      );

    default:
      return (
        <p className="part-text part-text--unknown">
          这条内容当前版本还展示不了，升级后可以看到。
        </p>
      );
  }
}

/** 同一条消息含多张图时合成紧凑网格；单图与其他富消息保持原来的渲染。 */
export function MessagePartsRenderer({
  parts,
  onRetrySkill,
  retryingSkillExecutionId,
  imagePreviewUrls,
  onImagePreviewConsumed,
}: {
  parts: readonly MessagePart[];
  onRetrySkill?: (executionId: string) => void;
  retryingSkillExecutionId?: string | null;
  imagePreviewUrls?: ReadonlyMap<string, string>;
  onImagePreviewConsumed?: (assetId: string, src: string) => void;
}) {
  const images = parts.filter(
    (part): part is Extract<MessagePart, { type: "image" }> => part.type === "image",
  );
  const firstImageIndex = parts.findIndex((part) => part.type === "image");

  return (
    <>
      {parts.map((part, index) => {
        if (part.type !== "image" || images.length <= 1) {
          return (
            <MessagePartRenderer
              key={`${part.type}-${index}`}
              part={part}
              {...(onRetrySkill ? { onRetrySkill } : {})}
              {...(retryingSkillExecutionId !== undefined
                ? { retryingSkillExecutionId }
                : {})}
              {...(imagePreviewUrls ? { imagePreviewUrls } : {})}
              {...(onImagePreviewConsumed ? { onImagePreviewConsumed } : {})}
            />
          );
        }
        if (index !== firstImageIndex) return null;
        return (
          <div className="part-images" key="image-grid">
            {images.map((image) => (
              <AssetImage
                key={image.assetId}
                assetId={image.assetId}
                alt={image.alt}
                {...(imagePreviewUrls?.get(image.assetId)
                  ? { initialSrc: imagePreviewUrls.get(image.assetId)! }
                  : {})}
                onInitialSrcConsumed={(src) =>
                  onImagePreviewConsumed?.(image.assetId, src)
                }
              />
            ))}
          </div>
        );
      })}
    </>
  );
}
